import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronRight,
  ClipboardList,
  FileText,
  Landmark,
  LogOut,
  UserCog,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { SIDEBAR_NAV, type SidebarGroup, type SidebarLeaf } from '@/config/routes';

export type SidebarMode = 'full' | 'rail' | 'drawer';

export interface SidebarProps {
  mode: SidebarMode;
  open?: boolean;
  onClose?: () => void;
  onCollapseToggle?: () => void;
  collapsed?: boolean;
}

function isLeafActive(pathname: string, to: string): boolean {
  if (pathname === to) return true;
  // Treat /automations/:slug/* as active under the queue leaf so the
  // sidebar highlight survives drilling into a detail row.
  return to !== '/' && pathname.startsWith(`${to}/`);
}

function groupHasActiveLeaf(group: SidebarGroup, pathname: string): boolean {
  return group.leaves.some((leaf) => isLeafActive(pathname, leaf.to));
}

const leafIcons: Record<string, LucideIcon> = {
  'ap-purchase-invoice': FileText,
  'admin-users': UserCog,
  'admin-audit': ClipboardList,
};

const groupIcons: Record<string, LucideIcon> = {
  finance: Landmark,
  admin: UserCog,
};

function LeafIcon({ leaf }: { leaf: SidebarLeaf }) {
  const Icon = leafIcons[leaf.id] ?? FileText;
  return <Icon className="h-16 w-16" aria-hidden="true" />;
}

function GroupIcon({ group }: { group: SidebarGroup }) {
  const Icon = groupIcons[group.id] ?? FileText;
  return <Icon className="h-16 w-16" aria-hidden="true" />;
}

interface NavGroupRowProps {
  group: SidebarGroup;
  pathname: string;
  expanded: boolean;
  onToggle: () => void;
  isRail: boolean;
  onLeafClick?: () => void;
}

function NavGroupRow({
  group,
  pathname,
  expanded,
  onToggle,
  isRail,
  onLeafClick,
}: NavGroupRowProps) {
  const hasActive = groupHasActiveLeaf(group, pathname);

  if (isRail) {
    return (
      <div className="flex flex-col items-center gap-2">
        {group.leaves.length > 1 && (
          <div
            className={cn(
              'flex h-40 w-40 items-center justify-center rounded-md',
              hasActive ? 'text-neutral-0' : 'text-neutral-500',
            )}
            title={group.label}
            aria-hidden="true"
          >
            <GroupIcon group={group} />
          </div>
        )}
        {group.leaves.map((leaf) => (
          <Link
            key={leaf.id}
            to={leaf.to}
            onClick={onLeafClick}
            title={leaf.label}
            aria-label={leaf.label}
            aria-current={isLeafActive(pathname, leaf.to) ? 'page' : undefined}
            className={cn(
              'flex h-40 w-40 items-center justify-center rounded-md transition-colors',
              isLeafActive(pathname, leaf.to)
                ? 'bg-primary-500/20 text-neutral-0'
                : 'text-neutral-300 hover:bg-neutral-800 hover:text-neutral-0',
            )}
          >
            <LeafIcon leaf={leaf} />
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          'flex w-full items-center gap-8 rounded-md px-12 py-8 text-xs font-semibold uppercase tracking-wide transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          hasActive ? 'text-neutral-0' : 'text-neutral-400 hover:text-neutral-200',
        )}
      >
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronRight
          className={cn(
            'h-12 w-12 shrink-0 transition-transform',
            expanded && 'rotate-90',
          )}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <ul className="mt-2 ml-8 space-y-2 border-l border-neutral-800 pl-8">
          {group.leaves.map((leaf) => (
            <li key={leaf.id}>
              <SidebarLeafLink
                leaf={leaf}
                active={isLeafActive(pathname, leaf.to)}
                onClick={onLeafClick}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SidebarLeafLink({
  leaf,
  active,
  onClick,
}: {
  leaf: SidebarLeaf;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      to={leaf.to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'block rounded-r-md px-12 py-8 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        active
          ? 'border-l-2 border-primary-500 bg-primary-500/15 pl-10 font-semibold text-neutral-0'
          : 'text-neutral-300 hover:bg-neutral-800 hover:text-neutral-0',
      )}
    >
      {leaf.label}
    </Link>
  );
}

export function Sidebar({
  mode,
  open,
  onClose,
  onCollapseToggle,
  collapsed,
}: SidebarProps) {
  const location = useLocation();
  const { signOut, user } = useAuth();
  const drawerRef = useRef<HTMLDivElement>(null);

  const isRail = mode === 'rail' || (mode === 'full' && !!collapsed);
  const widthClass = mode === 'drawer' ? 'w-[256px]' : isRail ? 'w-[72px]' : 'w-[256px]';
  const visibleNav = useMemo(
    () => SIDEBAR_NAV.filter((group) => group.id !== 'admin' || !!user?.is_admin),
    [user?.is_admin],
  );

  // Both groups expanded by default. Manual toggles override per-group;
  // active descendants force-open even if the user collapsed earlier.
  const initialExpanded = useMemo(() => {
    const set = new Set<string>();
    for (const g of visibleNav) set.add(g.id);
    return set;
  }, [visibleNav]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(initialExpanded);

  useEffect(() => {
    setExpandedGroups(initialExpanded);
  }, [initialExpanded]);

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Drawer focus-trap + Escape close
  useEffect(() => {
    if (mode !== 'drawer' || !open) return;
    const root = drawerRef.current;
    if (!root) return;
    const focusables = () =>
      Array.from(
        root.querySelectorAll<HTMLElement>(
          'a, button, [tabindex]:not([tabindex="-1"])',
        ),
      );
    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mode, open, onClose]);

  const closeOnLeafClick = mode === 'drawer' ? onClose : undefined;

  const body = (
    <nav
      ref={drawerRef}
      aria-label="Primary"
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-neutral-800 bg-neutral-900 text-neutral-100',
        widthClass,
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          'flex shrink-0 items-center border-b border-neutral-800 px-16 py-16',
          isRail && 'justify-center px-0',
        )}
      >
        <Link
          to="/"
          onClick={closeOnLeafClick}
        className="flex items-center gap-10 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <span className="flex h-32 w-32 shrink-0 items-center justify-center rounded-md bg-primary-500 text-base font-bold text-neutral-0">
            S
          </span>
          {!isRail && (
            <span className="leading-tight">
              <span className="block text-base font-bold tracking-tight text-neutral-0">
                ShahzadTex
              </span>
              <span className="block text-xs text-neutral-400">Approvals</span>
            </span>
          )}
        </Link>
      </div>

      {/* Nav groups */}
      <div
        className={cn(
          'flex-1 overflow-y-auto',
          isRail ? 'px-2 py-12' : 'px-12 py-12',
        )}
      >
        <div className={cn('flex flex-col', isRail ? 'gap-12' : 'gap-16')}>
          {visibleNav.map((group) => (
            <NavGroupRow
              key={group.id}
              group={group}
              pathname={location.pathname}
              expanded={expandedGroups.has(group.id)}
              onToggle={() => toggleGroup(group.id)}
              isRail={isRail}
              onLeafClick={closeOnLeafClick}
            />
          ))}
        </div>
      </div>

      {/* Footer: collapse toggle (full only) + sign out */}
      <div className="shrink-0 border-t border-neutral-800 p-8">
        {mode === 'full' && onCollapseToggle && (
          <button
            type="button"
            onClick={onCollapseToggle}
            className="mb-4 flex w-full items-center justify-center gap-8 rounded-md px-12 py-8 text-xs text-neutral-400 hover:bg-neutral-800 hover:text-neutral-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRight
              className={cn('h-12 w-12 transition-transform', !collapsed && 'rotate-180')}
              aria-hidden="true"
            />
            {!collapsed && <span>Collapse</span>}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            void signOut();
          }}
          className={cn(
            'flex w-full items-center gap-8 rounded-md px-12 py-8 text-sm text-neutral-300 transition-colors',
            'hover:bg-neutral-800 hover:text-neutral-0',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isRail && 'justify-center px-0',
          )}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-16 w-16 shrink-0" aria-hidden="true" />
          {!isRail && <span>Sign out</span>}
        </button>
      </div>
    </nav>
  );

  if (mode === 'drawer') {
    return (
      <div
        className={cn(
          'fixed inset-0 z-[--z-modal] transition-opacity',
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-hidden={!open}
      >
        <div
          onClick={onClose}
          className="absolute inset-0 bg-neutral-900/60"
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className={cn(
            'absolute inset-y-0 left-0 transition-transform',
            open ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          {body}
        </div>
      </div>
    );
  }

  return body;
}
